import React, { useEffect, useState, useMemo } from 'react';
import client from '../api/client';

interface DocFile {
  filename: string;
  type: 'Minutes' | 'Slides';
  dateLabel: string;
  sortDate: number;
}

interface DateGroup {
  dateLabel: string;
  sortDate: number;
  minutes: DocFile | null;
  slides: DocFile | null;
}

interface ModalState {
  group: DateGroup;
  summary: string | null;
  loadingSummary: boolean;
}

const MONTHS_ORDER = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

async function openFile(path: string, _filename?: string) {
  const response = await client.get(path, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: response.data.type });
  const url = URL.createObjectURL(blob);
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    window.location.href = url;
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function DateBadge({ dateLabel }: { dateLabel: string }) {
  const parts = dateLabel.trim().split(/\s+/);
  const month = parts.length >= 2 ? parts[parts.length - 2] : '';
  const year = parts.length >= 1 ? parts[parts.length - 1].slice(-2) : '';
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        background: 'var(--c-accent)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        flexShrink: 0,
        lineHeight: 1.2,
      }}
    >
      <span>{month}</span>
      <span>{year}</span>
    </div>
  );
}

export const DocumentsPage: React.FC = () => {
  const [groups, setGroups] = useState<DateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [filterMonth, setFilterMonth] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [summaries, setSummaries] = useState<Record<string, string | null>>({});

  useEffect(() => {
    client
      .get<{ summaries: Record<string, string | null> }>('/documents/summaries')
      .then(({ data }) => setSummaries(data.summaries))
      .catch(() => {});
  }, []);

  useEffect(() => {
    client
      .get<{ files: DocFile[] }>('/documents/files')
      .then(({ data }) => {
        const map = new Map<string, DateGroup>();
        for (const f of data.files) {
          if (!map.has(f.dateLabel)) {
            map.set(f.dateLabel, { dateLabel: f.dateLabel, sortDate: f.sortDate, minutes: null, slides: null });
          }
          const g = map.get(f.dateLabel)!;
          if (f.type === 'Minutes') g.minutes = f;
          else g.slides = f;
        }
        const sorted = Array.from(map.values()).sort((a, b) => b.sortDate - a.sortDate);
        setGroups(sorted);
      })
      .catch(() => setError('Failed to load documents. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  const availableYears = useMemo(() => {
    const source = filterMonth !== null
      ? groups.filter(g => g.dateLabel.split(' ').slice(-2)[0] === filterMonth)
      : groups;
    return [...new Set(source.map(g => Math.floor(g.sortDate / 10000)))].sort((a, b) => a - b);
  }, [groups, filterMonth]);

  const availableMonths = useMemo(() => {
    const source = filterYear !== null
      ? groups.filter(g => Math.floor(g.sortDate / 10000) === filterYear)
      : groups;
    const seen = new Set(source.map(g => g.dateLabel.split(' ').slice(-2)[0]));
    return MONTHS_ORDER.filter(m => seen.has(m));
  }, [groups, filterYear]);

  const filtered = useMemo(() => groups.filter(g => {
    const month = g.dateLabel.split(' ').slice(-2)[0];
    const year = Math.floor(g.sortDate / 10000);
    if (filterMonth !== null && month !== filterMonth) return false;
    if (filterYear !== null && year !== filterYear) return false;
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      const summary = g.minutes ? (summaries[g.minutes.filename] ?? '') : '';
      const inLabel = g.dateLabel.toLowerCase().includes(q);
      const inSummary = summary ? summary.toLowerCase().includes(q) : false;
      if (!inLabel && !inSummary) return false;
    }
    return true;
  }), [groups, filterMonth, filterYear, searchText, summaries]);

  const openModal = (group: DateGroup) => {
    setModal({ group, summary: null, loadingSummary: true });
    if (group.minutes) {
      client
        .get<{ summary: string | null }>(`/documents/summary/${encodeURIComponent(group.minutes.filename)}`)
        .then(({ data }) => setModal((m) => m ? { ...m, summary: data.summary, loadingSummary: false } : null))
        .catch(() => setModal((m) => m ? { ...m, summary: null, loadingSummary: false } : null));
    } else {
      setModal((m) => m ? { ...m, loadingSummary: false } : null);
    }
  };

  const closeModal = () => setModal(null);

  const handleDownload = async (file: DocFile, e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(file.filename);
    try {
      await openFile(`/documents/files/${encodeURIComponent(file.filename)}`, file.filename);
    } catch {
      alert('Could not open file. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
        <p style={{ fontSize: 15 }}>Loading meetings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
        <p style={{ fontSize: 15 }}>{error}</p>
      </div>
    );
  }

  const filtersActive = filterMonth !== null || filterYear !== null || searchText.trim() !== '';

  return (
    <>
      {/* Page heading */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 4 }}>Meetings</h2>
        <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
          Minutes and slides from RDF member meetings.
        </p>
      </div>

      {/* Filter bars */}
      {(availableYears.length > 1 || availableMonths.length > 1) && (
        <div style={{ marginBottom: 20 }}>
          {availableYears.length > 1 && (
            <div className="filter-bar">
              <button
                className={`filter-btn${filterYear === null ? ' active' : ''}`}
                onClick={() => setFilterYear(null)}
              >All</button>
              {availableYears.map(y => (
                <button
                  key={y}
                  className={`filter-btn${filterYear === y ? ' active' : ''}`}
                  onClick={() => setFilterYear(filterYear === y ? null : y)}
                >{y}</button>
              ))}
            </div>
          )}
          {availableMonths.length > 1 && (
            <div className="filter-bar">
              <button
                className={`filter-btn${filterMonth === null ? ' active' : ''}`}
                onClick={() => setFilterMonth(null)}
              >All</button>
              {availableMonths.map(m => (
                <button
                  key={m}
                  className={`filter-btn${filterMonth === m ? ' active' : ''}`}
                  onClick={() => setFilterMonth(filterMonth === m ? null : m)}
                >{m}</button>
              ))}
            </div>
          )}
          <input
            type="text"
            placeholder="Search meeting content… e.g. UPI, EMIR, collateral"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 12px',
              fontSize: 13,
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--r)',
              background: 'var(--c-surface)',
              color: 'var(--c-text)',
              outline: 'none',
              marginTop: 4,
            }}
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
          <p style={{ fontSize: 15 }}>{filtersActive ? 'No meetings found for the selected filters.' : 'No meetings found.'}</p>
        </div>
      ) : (
        <div className="doc-grid">
          {filtered.map((g) => (
            <div
              key={g.dateLabel}
              className="doc-card"
              onClick={() => openModal(g)}
              style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <DateBadge dateLabel={g.dateLabel} />
                <div className="doc-info" style={{ flex: 1 }}>
                  <div className="doc-title">{g.dateLabel}</div>
                  <div className="doc-meta">Minutes &amp; Slides</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                {g.slides && (
                  <button
                    onClick={(e) => handleDownload(g.slides!, e)}
                    disabled={downloading === g.slides.filename}
                    className="badge badge-neutral"
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer', border: 'none' }}
                  >
                    {downloading === g.slides.filename ? '...' : 'Slides'}
                  </button>
                )}
                {g.minutes && (
                  <button
                    onClick={(e) => handleDownload(g.minutes!, e)}
                    disabled={downloading === g.minutes.filename}
                    className="badge badge-neutral"
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer', border: 'none' }}
                  >
                    {downloading === g.minutes.filename ? '...' : 'Minutes'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: 16 }}>{modal.group.dateLabel}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {modal.loadingSummary ? (
                <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>Loading summary...</p>
              ) : modal.summary ? (
                <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{modal.summary}</div>
              ) : (
                <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>Summary not available</p>
              )}
            </div>
            <div className="modal-footer">
              {modal.group.slides && (
                <button
                  onClick={(e) => handleDownload(modal.group.slides!, e)}
                  disabled={downloading === modal.group.slides.filename}
                  className="badge badge-neutral"
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  {downloading === modal.group.slides.filename ? '...' : 'Slides'}
                </button>
              )}
              {modal.group.minutes && (
                <button
                  onClick={(e) => handleDownload(modal.group.minutes!, e)}
                  disabled={downloading === modal.group.minutes.filename}
                  className="badge badge-neutral"
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  {downloading === modal.group.minutes.filename ? '...' : 'Minutes'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
